import {Dexie} from 'dexie';
import {IVanillaReaderOptionsData} from '../VanillaReader/VanillaReaderModel';
import {IVanillaReaderUiOptionsData} from '../VanillaReaderUI/VanillaReaderUiModel';
import {IVanillaReaderOptionsDataStore} from './IVanillaReaderOptionsDataStore';
import {IVanillaReaderOptionsStoreData, IVanillaReaderStoreUiOptionsData} from "./VanillaReaderStoreModel";
import {VanillaReaderOptionsIndexedDbDataStoreConversions} from "./VanillaReaderOptionsIndexedDbDataStoreConversions";

/*
* # VanillaReaderIndexedDbDataStore
*
* ## RESPONSIBILITIES
*
* This is an implementation of the `IVanillaReaderDataStore` interface that uses IndexedDb. IndexedDb is the preferred
* way to store web app data, However since its APIs are asynchronous it is not possible to use to store state when
* the application is closed by the system, or when it is put in the background on mobile devices.
*
* I suggest using it in combination with the `VanillaReaderLocalStorageDataStore` to get both a fail-safe temp storage,
* as well as a long-term storage.
*
* Checkout the `IVanillaReaderDataStore` interface documentation for more general information.
*
* */

export class VanillaReaderOptionsIndexedDbDataStore extends Dexie implements IVanillaReaderOptionsDataStore {

    private _vanillaReaderOptionsData: Dexie.Table<IVanillaReaderOptionsStoreData, number>;
    private _vanillaReaderUiOptionsData: Dexie.Table<IVanillaReaderStoreUiOptionsData, number>;

    private _initialized: boolean = false;
    constructor(dbName: string = 'options.vanillareader.colibrio.com', persist: boolean = true) {
        super(dbName);

        if (persist && navigator.storage && navigator.storage.persist) {
            navigator.storage.persist().then((canPersist) => {
                if (canPersist) {
                    console.log('VanillaReaderSettingsIndexedDbDataStore will persist data.');
                } else {
                    console.log('VanillaReaderSettingsIndexedDbDataStore will NOT persist data.');
                }
            }).catch(console.warn);
        }

        this.version(1).stores({
            vanillaReaderOptionsData: '_id, dateCreated',
            vanillaReaderUiOptionsData: '_id, dateCreated',
        });

        this._vanillaReaderOptionsData = this.table('vanillaReaderOptionsData');
        this._vanillaReaderUiOptionsData = this.table('vanillaReaderUiOptionsData');
    }

    initialize(): Promise<void> {
        return this.open().then(_db => {
            this._initialized = true
        }).catch(_ => {
            this._initialized = false
        });
    }

    async fetchVanillaReaderOptionsData(): Promise<IVanillaReaderOptionsData | undefined> {
        if (this._initialized){
            return this._vanillaReaderOptionsData.toCollection().last().then(VanillaReaderOptionsIndexedDbDataStoreConversions.maybeToApplicationData);
        } else {
            return;
        }
    }

    async setVanillaReaderOptionsData(state: IVanillaReaderOptionsData): Promise<void> {
        if (this._initialized) {
            // There should only be one
            let existingOptionCount = await this._vanillaReaderOptionsData.count();
            if (existingOptionCount > 1) {
                await this._vanillaReaderOptionsData.clear();
            }

            state._id = 1;
            const storeState = VanillaReaderOptionsIndexedDbDataStoreConversions.toStoreData(state);
            await this._vanillaReaderOptionsData.put(storeState, 1);
        }
    }

    async clearVanillaReaderOptionsData(): Promise<void> {
        if (this._initialized) {
            await this._vanillaReaderOptionsData.clear();
        }
    }

    async fetchVanillaReaderUiOptionsData(): Promise<IVanillaReaderUiOptionsData | undefined> {
        if (this._initialized) {
            return this._vanillaReaderUiOptionsData.toCollection().last().then(VanillaReaderOptionsIndexedDbDataStoreConversions.maybeToUiApplicationData)
        } else {
            return undefined;
        }
    }

    async setVanillaReaderUiOptionsData(state: IVanillaReaderUiOptionsData): Promise<void> {
        if (this._initialized) {
            // There should only be one
            let existingOptionCount = await this._vanillaReaderUiOptionsData.count();
            if (existingOptionCount > 1) {
                await this._vanillaReaderUiOptionsData.clear();
            }
            state._id = 1;
            const storeState = VanillaReaderOptionsIndexedDbDataStoreConversions.toStoreUiOptionsData(state);
            await this._vanillaReaderUiOptionsData.put(storeState, 1);
        }
    }

    async clearVanillaReaderUiOptionsData(): Promise<void> {
        if (this._initialized) {
            return this._vanillaReaderUiOptionsData.clear();
        }
    }


}
